import { CallId } from 'shared/models';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'reducers';
import { toError } from 'shared/util/toError';
import { getVideoCallEvents } from "selectors/ecsSelectors";
import { callApi } from 'apiClient';
import { getVideoCallEventsSuccess } from 'actions/ecsActions';

export function useEcsCallEvents(callId: CallId) {
    const [ busy, setBusy ] = useState(false);
    const [ errKey, setErrKey ] = useState('');
    const mountedRef = useRef(true);

    const callEvents = useSelector((state: State) => getVideoCallEvents(state, { callId, frames: true }));

    // Keep an immutable ref handle to the mutable data so our functions that depend on this data
    // can remain immutable themselves (i.e. don't want the function to change when the data changes).
    const callEventsRef = useRef(callEvents);

    // Update the immutable ref when the data changes
    useEffect(() => {
        callEventsRef.current = callEvents;
    }, [ callEvents, callEventsRef ]);

    const dispatch = useDispatch();

    const loadEvents = useCallback(async () => {
        try {
            setBusy(true);
            setErrKey('');

            let lastEventId = 0;
            let done = false;
            while (!done && mountedRef.current) {
                const callEvents = callEventsRef.current;
                if (callEvents) {
                    if (lastEventId === callEvents.slice(-1)[0].id) {
                        // this should never happen... just a failsafe to prevent unnecessary looping in case a bizarre error happens
                        break;
                    }
                    lastEventId = callEvents.slice(-1)[0].id;
                }

                const queryStringParts = [
                    callEvents
                        ? `since=${lastEventId}`
                        : '',
                    'frames=1',
                ].filter(p => !!p);
        
                const queryString =
                    queryStringParts.length
                        ? `?${queryStringParts.join('&')}`
                        : '';

                const { events, more } = await callApi('GET', `/api/video/${callId}/events${queryString}`);

                // Temporarily add the current batch of events into our master list.
                // Note: the array reference will change later when Redux incorporates
                // the new events and then our useEffect runs and grabs the latest copy.
                callEventsRef.current = [
                    ...(callEvents || []),
                    ...events,
                ];

                // Pass the frames to Redux
                dispatch(getVideoCallEventsSuccess(events, { callId, more, frames: true }));

                done = !more;
            }
            return callEventsRef.current;
        }
        catch (e) {
            const err = toError(e);
            setErrKey(err.message);
            return null;
        }
        finally {
            setBusy(false);
        }
    }, [ setBusy, setErrKey, callEventsRef, mountedRef, dispatch ]);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, [ mountedRef ]);

    return {
        busy,
        errKey,
        callEvents,
        loadEvents,
    };
}
